/**
 * «Переработка» dialog — overtime plus delta (task 23 §5.3).
 *
 * Overtime always carries a project (Zod requires `project_id`, the backend
 * CHECK enforces it again §5.1) and a positive `hours` value. RHF numeric
 * fields use `valueAsNumber`, never `z.coerce.number()` (frontend/CLAUDE.md).
 */
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/ui/date-field";
import { Combobox } from "@/components/ui/combobox";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showApiErrorToast } from "@/lib/api-error-toast";
import {
  useCreateScheduleEvent,
  usePersonEventTypes,
  useUpdateScheduleEvent,
} from "@/hooks/usePeople";
import { useProjects } from "@/hooks/useProjects";
import type { PersonScheduleEvent } from "@/api/people";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import {
  apiErrorFormIssue,
  FormErrorSummary,
  FormFieldError,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";

export interface OvertimeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: number;
  initialEvent?: PersonScheduleEvent;
}

const isoDate = /^\d{4}-\d{2}-\d{2}$/;

const formSchema = z
  .object({
    // 0 is the empty sentinel for «no project picked» — refined away below.
    project_id: z.number().int(),
    event_type_id: z.number().int().positive(),
    date_from: z.string().regex(isoDate),
    date_to: z.string().regex(isoDate),
    hours: z.number().multipleOf(0.5, "hoursRange").min(0.5, "hoursPositive").max(24, "hoursRange"),
    payable: z.boolean(),
    reason: z.string().max(500),
  })
  .refine((v) => v.project_id > 0, { path: ["project_id"], message: "projectRequired" })
  .refine((v) => v.date_to >= v.date_from, { path: ["date_to"], message: "dateOrder" });

type OvertimeValues = z.infer<typeof formSchema>;

const DEFAULTS: OvertimeValues = {
  project_id: 0,
  event_type_id: 0,
  date_from: "",
  date_to: "",
  hours: 1,
  payable: true,
  reason: "",
};

export function OvertimeFormDialog({
  open,
  onOpenChange,
  userId,
  initialEvent,
}: OvertimeFormDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const createMut = useCreateScheduleEvent();
  const updateMut = useUpdateScheduleEvent();
  const eventTypesQuery = usePersonEventTypes();
  const projectsQuery = useProjects({ limit: 200 });
  const [serverError, setServerError] = useState<FormValidationIssue | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<OvertimeValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULTS,
  });
  useReleaseDirtyState(open && isDirty);

  useEffect(() => {
    if (open) {
      setServerError(null);
      reset(
        initialEvent
          ? {
              project_id: initialEvent.project_id ?? 0,
              event_type_id: initialEvent.event_type_id,
              date_from: initialEvent.date_from,
              date_to: initialEvent.date_to,
              hours: (initialEvent.duration_half_hours ?? 0) / 2,
              payable: initialEvent.payable,
              reason: initialEvent.reason,
            }
          : DEFAULTS,
      );
    }
  }, [initialEvent, open, reset]);

  const overtimeTypes = (eventTypesQuery.data ?? []).filter(
    (item) =>
      item.event_group === "overtime" &&
      (item.is_active || item.id === initialEvent?.event_type_id),
  );
  // eslint-disable-next-line react-hooks/incompatible-library
  const eventTypeId = watch("event_type_id");
  useEffect(() => {
    if (!open || initialEvent || eventTypeId > 0 || overtimeTypes.length === 0) return;
    setValue("event_type_id", overtimeTypes[0].id, { shouldValidate: true });
  }, [eventTypeId, initialEvent, open, overtimeTypes, setValue]);

  async function onSubmit(values: OvertimeValues): Promise<void> {
    setServerError(null);
    const eventType = overtimeTypes.find((item) => item.id === values.event_type_id);
    if (!eventType) {
      const message = t("people.toast.addFailed");
      setServerError({ id: "overtime-form-server-error", message, kind: "server" });
      toast.error(message);
      return;
    }
    try {
      const body = {
        date_from: values.date_from,
        date_to: values.date_to,
        event_type_id: eventType.id,
        is_all_day: false,
        duration_half_hours: values.hours * 2,
        project_id: values.project_id,
        payable: values.payable,
        reason: values.reason,
      };
      if (initialEvent) {
        await updateMut.mutateAsync({ userId, eventId: initialEvent.id, body });
      } else {
        await createMut.mutateAsync({ userId, body });
      }
      toast.success(t("people.toast.added"));
      onOpenChange(false);
    } catch (err) {
      setServerError(
        apiErrorFormIssue(err, {
          id: "overtime-form-server-error",
          fallbackMessage: t("common.apiError.fallback"),
        }),
      );
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    }
  }

  function describeError(name: keyof OvertimeValues): string | undefined {
    const msg = errors[name]?.message;
    if (!msg) return undefined;
    if (msg === "projectRequired") return t("people.errors.projectRequired");
    if (msg === "dateOrder") return t("people.errors.dateOrder");
    if (msg === "hoursPositive") return t("people.errors.hoursPositive");
    if (msg === "hoursRange") return t("people.errors.hoursRange");
    return t("people.errors.required");
  }

  const issues: FormValidationIssue[] = [];
  for (const [name, controlId] of [
    ["event_type_id", "overtime-field-kind"],
    ["project_id", "overtime-field-project"],
    ["date_from", "overtime-field-date-from"],
    ["date_to", "overtime-field-date-to"],
    ["hours", "overtime-field-hours"],
  ] as const) {
    const message = describeError(name);
    if (message) {
      issues.push({ id: `${controlId}-error`, message, kind: "field", controlId });
    }
  }
  if (serverError) issues.push(serverError);

  function issueFor(name: keyof OvertimeValues): FormValidationIssue | undefined {
    const controlIds: Partial<Record<keyof OvertimeValues, string>> = {
      event_type_id: "overtime-field-kind",
      project_id: "overtime-field-project",
      date_from: "overtime-field-date-from",
      date_to: "overtime-field-date-to",
      hours: "overtime-field-hours",
    };
    return issues.find((issue) => issue.id === `${controlIds[name]}-error`);
  }

  function handleInvalid(invalidErrors: typeof errors): void {
    const controlId = [
      ["event_type_id", "overtime-field-kind"],
      ["project_id", "overtime-field-project"],
      ["date_from", "overtime-field-date-from"],
      ["date_to", "overtime-field-date-to"],
      ["hours", "overtime-field-hours"],
    ].find(([name]) => invalidErrors[name as keyof OvertimeValues])?.[1];
    if (controlId) requestAnimationFrame(() => focusValidationControl(controlId));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="overtime-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {t(initialEvent ? "people.overtimeForm.editTitle" : "people.overtimeForm.title")}
          </DialogTitle>
          <DialogDescription>{t("people.overtimeForm.desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit, handleInvalid)} className="space-y-3" noValidate>
          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("people.overtimeForm.kind")}
            </span>
            <Select
              id="overtime-field-kind"
              data-testid="overtime-field-kind"
              value={eventTypeId > 0 ? String(eventTypeId) : ""}
              onChange={(value) =>
                setValue("event_type_id", Number(value), {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              placeholder={t("people.overtimeForm.kind")}
              className="h-8"
              options={overtimeTypes.map((item) => ({ value: String(item.id), label: item.name }))}
              aria-invalid={Boolean(issueFor("event_type_id"))}
              aria-describedby={issueFor("event_type_id")?.id}
            />
            <FormFieldError issue={issueFor("event_type_id")} />
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("people.overtimeForm.project")}
            </span>
            <Controller
              control={control}
              name="project_id"
              render={({ field }) => (
                <Combobox
                  id="overtime-field-project"
                  data-testid="overtime-field-project"
                  className="h-8"
                  options={(projectsQuery.data ?? [])
                    .filter((p) => p.id != null)
                    .map((p) => ({ value: String(p.id), label: p.name }))}
                  value={field.value > 0 ? String(field.value) : null}
                  onChange={(v) => field.onChange(v === null ? 0 : Number(v))}
                  placeholder={t("people.overtimeForm.projectPlaceholder")}
                  searchPlaceholder={t("common.combobox.searchPlaceholder")}
                  emptyText={t("common.combobox.empty")}
                  aria-invalid={Boolean(issueFor("project_id"))}
                  aria-describedby={issueFor("project_id")?.id}
                />
              )}
            />
            <FormFieldError issue={issueFor("project_id")} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="text-muted-foreground mb-1 block text-xs">
                {t("people.overtimeForm.dateFrom")}
              </span>
              <Controller
                control={control}
                name="date_from"
                render={({ field }) => (
                  <DateField
                    id="overtime-field-date-from"
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? "")}
                    label={t("people.overtimeForm.dateFrom")}
                    name={field.name}
                    required
                    data-testid="overtime-field-date-from"
                    aria-invalid={Boolean(issueFor("date_from"))}
                    aria-describedby={issueFor("date_from")?.id}
                  />
                )}
              />
              <FormFieldError issue={issueFor("date_from")} />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground mb-1 block text-xs">
                {t("people.overtimeForm.dateTo")}
              </span>
              <Controller
                control={control}
                name="date_to"
                render={({ field }) => (
                  <DateField
                    id="overtime-field-date-to"
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? "")}
                    label={t("people.overtimeForm.dateTo")}
                    name={field.name}
                    required
                    data-testid="overtime-field-date-to"
                    aria-invalid={Boolean(issueFor("date_to"))}
                    aria-describedby={issueFor("date_to")?.id}
                  />
                )}
              />
              <FormFieldError issue={issueFor("date_to")} />
            </label>
          </div>

          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("people.overtimeForm.hours")}
            </span>
            {/* No HTML min/max: native constraint validation would silently
                block submit in some browsers/jsdom before RHF runs, so the Zod
                error never surfaces. Zod owns the bounds. */}
            <Input
              id="overtime-field-hours"
              type="number"
              step={0.5}
              data-testid="overtime-field-hours"
              {...register("hours", { valueAsNumber: true })}
              aria-invalid={Boolean(issueFor("hours"))}
              aria-describedby={issueFor("hours")?.id}
            />
            <FormFieldError issue={issueFor("hours")} />
          </label>

          <Controller
            control={control}
            name="payable"
            render={({ field }) => (
              <label
                htmlFor="overtime-field-payable"
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <Switch
                  id="overtime-field-payable"
                  data-testid="overtime-field-payable"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span>{t("people.overtimeForm.payable")}</span>
              </label>
            )}
          />

          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("people.overtimeForm.reason")}
            </span>
            <Input data-testid="overtime-field-reason" {...register("reason")} />
          </label>

          <FormErrorSummary
            issues={issues}
            countLabel={(count) => t("validation.fieldErrorCount", { count })}
            data-testid="overtime-form-error-summary"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="overtime-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="overtime-form-submit">
              {t(initialEvent ? "common.save" : "people.overtimeForm.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
